import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getGroupById, getGroupWorkspacesByGroupId } from "@/db/groups"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.groups', supabase)

type Row = Tables<'groups'>
type WsRow = Tables<'group_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const groupWorkspaces = await getGroupWorkspacesByGroupId(supabase, id)
	return groupWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setGroups
 * @returns
 */
export function makeGroupChangeHandler(
	setGroups: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `group-changes-${uuid}`,
		channelName: `group-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'groups',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const group = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setGroups(prev => {
					const exists = prev.some(p => p.id === group.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling group update:${printObject(group)}`)
					return prev.map((p: any) => p.id === group.id ? group : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setGroups
 * @returns
 */
export function makeGroupWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setGroups: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `group-ws-changes-${uuid}`,
		channelName: `group-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'group_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const groupWs = payload.new as WsRow
				const id = groupWs.group_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const group = await getGroupById(supabase, id)
					if (group) {
						setGroups(prev => {
 							if (prev.some(p => p.id === group.id)) return prev
							logger.info(`Handling group Insert:${printObject(group)}`)
 							return [...prev, group]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const groupWs = payload.old as WsRow
				const id = groupWs.group_id
				setGroups(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling group Delete:${id}`)
					return prev.filter((prevItem: any) => prevItem.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}